import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartPie, 
  faChartBar, 
  faUsers, 
  faListAlt, 
  faTicketAlt 
} from '@fortawesome/free-solid-svg-icons';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { 
  FaChartPie as FaChartPieIcon,
  FaCheckCircle, 
  FaLayerGroup, 
  FaHourglassHalf, 
  FaUserPlus, 
  FaCalendarAlt, 
  FaMedal, 
  FaGraduationCap, 
  FaProjectDiagram,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaUsers as FaUsersIcon,
  FaUserGraduate,
  FaChartLine,
  FaExclamationTriangle
} from 'react-icons/fa';

// Components
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { useTheme } from '../theme/ThemeProvider';
import ProjectActivityChart from '../components/ProjectActivityChart';
import MemberGrowthLineChart from '../components/MemberGrowthLineChart';
import {
  EARLIEST_COUNCIL_YEAR,
  getCouncilYearOptions,
  getCurrentCouncilYearStart,
  isDateInCouncilYear,
  isLikelyGraduatedBySliitId,
} from '../utils/leoYear';
import { getFlaggedMembers } from '../utils/memberQuality';
import usePolling from '../hooks/usePolling';

const LIGHT_BACKGROUND_THEMES = new Set(['executive-light', 'leo-legacy']);

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);


// Helper: generate monthly growth data from members
function generateMemberGrowthData(members) {
  if (!Array.isArray(members)) return [];
  // Group by month
  const counts = {};
  members.forEach(m => {
    if (!m.RegDate) return;
    const d = new Date(m.RegDate);
    if (isNaN(d)) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    counts[key] = (counts[key] || 0) + 1;
  });
  // Sort months and accumulate
  const months = Object.keys(counts).sort();
  let cumulative = 0;
  return months.map(month => {
    cumulative += counts[month];
    return { month, count: cumulative };
  });
}

export default function ClubStatistics() {
  const { theme } = useTheme();
  const toastTheme = LIGHT_BACKGROUND_THEMES.has(theme) ? 'light' : 'dark';

  // State declarations
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png',
  };

  // Calculate yearly growth data
  const yearlyGrowthData = useMemo(() => {
    const years = {};
    
    members.forEach(member => {
      if (!member.RegDate) return;
      
      const year = new Date(member.RegDate).getFullYear();
      if (!years[year]) {
        years[year] = 0;
      }
      years[year]++;
    });
    
    // Sort years in ascending order
    const sortedYears = {};
    Object.keys(years).sort().forEach(key => {
      sortedYears[key] = years[key];
    });
    
    return sortedYears;
  }, [members]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'RegDate', direction: 'desc' });
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const API_BASE = 'https://dev.sliitleo.org/Backend';

  // `silent` skips the loading spinner -- used by the background poll below
  // so the dashboard refreshes on its own without flashing back to a
  // skeleton state every cycle.
  const loadMembers = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    // Explicit limit: the endpoint defaults to 4000 rows when none is passed,
    // which was silently truncating the real roster (4085+ members) -- every
    // stat on this page (total membership, year-level counts, Leostic-year
    // breakdown) was undercounting as a result. 40000 matches the backend's
    // own hard cap, comfortably above any realistic roster size.
    return fetch(`${API_BASE}/member_api.php?action=list_basic&limit=40000`, {
      headers: { Accept: 'application/json' }
    })
      .then(async (res) => {
        // Handle HTTP errors BEFORE trying to parse JSON
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`member_api ${res.status} ${res.statusText} ${text}`);
        }
        return res.json();
      })
      .then((payload) => {
        // Support either a raw array OR {success, data}
        const arr = Array.isArray(payload) ? payload
                 : (payload && Array.isArray(payload.data)) ? payload.data
                 : [];
        setMembers(arr);
      })
      .catch((err) => {
        console.error('member_api failed:', err);
        if (!silent) toast.error('Failed to load member data. Please try again later.');
      })
      .finally(() => { if (!silent) setLoading(false); });
  }, [API_BASE]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  // Live-refresh the roster in the background so new members, restrictions,
  // etc. show up here without a manual reload. Slower than Club Projects/
  // Admin Console since this page fetches the full ~4000+ row roster.
  usePolling(() => loadMembers(true), { intervalMs: 45000, hiddenIntervalMs: 120000 });


  // Debounce search input
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1);
    }, 300);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchQuery]);

  // Filter and sort members
  const filteredMembers = useMemo(() => {
    let result = [...members];
    
    // Debug: Log first member to check structure
  
    // Apply search filter
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase().trim();
      console.log('Searching for:', query);
      
      result = result.filter(member => {
        // Get SLIIT ID (checking common field names)
        const sliitId = (
          member.SLIITID || 
          member.sliitId || 
          member.studentId || 
          member.StudentID || 
          ''
        ).toString().toLowerCase().trim();
        
        // Get full name (checking common field names)
        const fullName = (
          member.Name || 
          member.name || 
          `${member.FirstName || ''} ${member.LastName || ''}`.trim()
        ).toLowerCase();
        
        // Get all member fields for search
        const memberFields = {
          name: fullName,
          email: (member.Email || member.email || '').toLowerCase(),
          phone: (member.Phone || member.phone || '').toLowerCase(),
          department: (member.Department || member.department || '').toLowerCase(),
          id: (member.id || member.ID || '').toString().toLowerCase(),
          rollNo: (member.RollNo || member.rollNo || '').toLowerCase(),
          batch: (member.Batch || member.batch || '').toLowerCase(),
          section: (member.Section || member.section || '').toLowerCase(),
          sliitId: sliitId
        };
        
        // Combine all fields into a single search string
        const searchString = Object.values(memberFields).join(' ');
        
        // Debug: Log search details for first few matches
        if (result.indexOf(member) < 3) {
          console.log('Searching member:', { 
            name: memberFields.name, 
            sliitId: memberFields.sliitId,
            searchString 
          });
        }
        
        // Check if query matches SLIIT ID exactly (without spaces)
        if (sliitId && sliitId.replace(/\s+/g, '') === query.replace(/\s+/g, '')) {
          console.log('Found by SLIIT ID match:', sliitId);
          return true;
        }
        
        // Check if query matches full name exactly (case insensitive)
        if (fullName && fullName === query.toLowerCase()) {
          console.log('Found by full name match:', fullName);
          return true;
        }

        // Check if all search terms are found in any field
        const searchTerms = query.split(/\s+/);
        const allTermsMatch = searchTerms.every(term => 
          Object.values(memberFields).some(field => 
            field.includes(term)
          )
        );
        
        if (allTermsMatch) {
          console.log('Found by partial match for terms:', searchTerms);
        }
        
        return allTermsMatch;
      });
    }
    
    // Filter by academic year (July to June)
    if (selectedYear) {
      const [startYear] = selectedYear.split('-').map(Number);
      const startDate = new Date(startYear, 6, 1); // July 1 of start year
      const endDate = new Date(startYear + 1, 6, 1); // July 1 of next year
      
      result = result.filter(member => {
        // If member has an academicYear field that matches, include them
        if (member.academicYear && member.academicYear === selectedYear) {
          return true;
        }
        
        // If no registration date, exclude
        if (!member.RegDate) return false;
        
        // Parse the registration date
        let regDate;
        try {
          regDate = new Date(member.RegDate);
          // If date is invalid, exclude
          if (isNaN(regDate.getTime())) return false;
        } catch (e) {
          return false;
        }
        
        // Check if registration date falls within the academic year
        return regDate >= startDate && regDate < endDate;
      });
      
      // console.log(`Filtered for ${selectedYear} (${startDate.toDateString()} - ${new Date(endDate - 1).toDateString()}):`, result.length, 'members');
    }
    
    // Apply sorting
    // replace your sort block with:
if (sortConfig.key) {
  result.sort((a, b) => {
    const av = (a?.[sortConfig.key] ?? '').toString().toLowerCase();
    const bv = (b?.[sortConfig.key] ?? '').toString().toLowerCase();

    if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
    if (av > bv) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });
}

    
    return result;
  }, [members, debouncedSearchQuery, selectedYear, sortConfig]);
  
  // Pagination logic
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);
  const currentItems = filteredMembers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  // Handle sort request
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting
  };
  
  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ml-1 text-gray-400" />;
    return sortConfig.direction === 'asc' 
      ? <FaSortUp className="ml-1 text-violet-400" /> 
      : <FaSortDown className="ml-1 text-violet-400" />;
  };
  
  // Export data function (to be implemented)
  const handleExport = () => {
    // This will be implemented when we add the export functionality
    // console.log('Exporting data...');
  };

  // Helper function to check if a date is within the current academic year (July to June)
  const isInCurrentAcademicYear = (date) => {
    if (!date) return false;
    const d = new Date(date);
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth(); // 0-11 (Jan-Dec)
    
    // Academic year starts in July (month 6)
    const academicYearStart = new Date(currentYear, 6, 1); // July 1 of current year
    const academicYearEnd = new Date(currentYear + 1, 5, 30); // June 30 of next year
    
    // If current month is before July, adjust the academic year
    if (currentMonth < 6) {
      academicYearStart.setFullYear(currentYear - 1);
      academicYearEnd.setFullYear(currentYear);
    }
    
    return d >= academicYearStart && d <= academicYearEnd;
  };

  // Compute statistics using current date
  const now = new Date();
  const oneWeekAgo = new Date(now);
  oneWeekAgo.setDate(now.getDate() - 7);
  
  const total = members.length;
  
  // New members this week
  const newcomersWeek = members.filter(m => {
    if (!m.RegDate) return false;
    const regDate = new Date(m.RegDate);
    return regDate >= oneWeekAgo && regDate <= now;
  }).length;
  
  // New members this month (current calendar month)
  const newcomersMonth = members.filter(m => {
    if (!m.RegDate) return false;
    const regDate = new Date(m.RegDate);
    return regDate.getMonth() === now.getMonth() && 
           regDate.getFullYear() === now.getFullYear();
  }).length;
  
  // Use the current date for academic year calculations
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth(); // 0-11 (Jan=0)
  
  // Determine current academic year (July to June)
  const academicYearStart = new Date(
    currentMonth >= 6 ? currentYear : currentYear - 1, // If month is July or later, use current year, else previous year
    6, // July (0-indexed)
    1 // 1st day of month
  );
    
  const academicYearEnd = new Date(
    academicYearStart.getFullYear() + 1, // Next year
    5, // June (0-indexed)
    30 // Last day of June
  );
  
  // Calculate new members in current academic year
  const newcomersAcademicYear = members.filter(m => {
    if (!m.RegDate) return false;
    const regDate = new Date(m.RegDate);
    return regDate >= academicYearStart && regDate <= academicYearEnd;
  }).length;
  
  // Format academic year string (e.g., "2025-26")
  const academicYearStr = `${academicYearStart.getFullYear()}-${(academicYearStart.getFullYear() + 1).toString().slice(-2)}`;

  // Initialize counters for year levels and graduates
  let year1Count = 0, year2Count = 0, year3Count = 0, year4Count = 0, graduatesCount = 0;
  
  members.forEach(member => {
    // Declare regDate at the start of the loop
    let regDate = null;
    
    // Check if member is explicitly marked as graduate
    const isExplicitGraduate = (member.Year && member.Year.toString().toLowerCase().includes('grad')) || 
                             (member.Status && member.Status.toString().toLowerCase().includes('grad'));
    
    // If no registration date, check explicit graduate status only
    if (!member.RegDate || member.RegDate === '0000-00-00' || member.RegDate === '0000-00-00 00:00:00') {
      if (isExplicitGraduate) graduatesCount++;
      return;
    }
    
    try {
      regDate = new Date(member.RegDate);
      if (isNaN(regDate.getTime())) {
        if (isExplicitGraduate) graduatesCount++;
        return;
      }
      
      // Calculate years since registration
      let yearsDiff = currentYear - regDate.getFullYear();
      
      // Adjust for month (if current month is before registration month, subtract a year)
      if (currentMonth < regDate.getMonth()) {
        yearsDiff--;
      }
      
      // Determine year level based on years since registration
      if (yearsDiff >= 4) {
        graduatesCount++;
      } else if (yearsDiff === 3) {
        year4Count++;
      } else if (yearsDiff === 2) {
        year3Count++;
      } else if (yearsDiff === 1) {
        year2Count++;
      } else if (yearsDiff === 0) {
        year1Count++;
      } else if (isExplicitGraduate) {
        // Handle case where registration date is in the future but marked as graduate
        graduatesCount++;
      }
    } catch (e) {
      // If there's an error parsing the date, just check explicit graduate status
      if (isExplicitGraduate) graduatesCount++;
      return; // Skip to next iteration if there was an error
    }
    
    // If registration date is valid, proceed with classification
    try {
      // Calculate academic year based on registration date (July to June)
      const regYear = regDate.getFullYear();
      const regMonth = regDate.getMonth(); // 0-11 (Jan-Dec)
      
      // Academic year starts in July (month 6)
      const academicStartYear = regMonth < 6 ? regYear - 1 : regYear;
      
      // Current academic year (July 2025 - June 2026 is 2025 academic year)
      const currentAcademicYear = currentMonth < 6 ? currentYear - 1 : currentYear;
      
      // Years since registration (1 = first year, 2 = second year, etc.)
      const yearsSinceRegistration = currentAcademicYear - academicStartYear + 1;
      
      // Categorize member based on years since registration
      if (isExplicitGraduate || (yearsSinceRegistration > 4 && regDate < new Date('2025-07-01'))) {
        graduatesCount++;
      } else if (yearsSinceRegistration === 1) {
        year1Count++;
      } else if (yearsSinceRegistration === 2) {
        year2Count++;
      } else if (yearsSinceRegistration === 3) {
        year3Count++;
      } else if (yearsSinceRegistration === 4) {
        year4Count++;
      }
    } catch (e) {
      // console.error('Error processing member registration date:', e);
      // If there's an error, just check explicit graduate status
      if (isExplicitGraduate) graduatesCount++;
    }
  });

  // Registration year-based groups
  const regYears = members.map(m => m.RegDate ? new Date(m.RegDate).getFullYear() : null).filter(y => y);
  const registrationYear = now.getFullYear();
  const prevYears = [registrationYear, registrationYear-1, registrationYear-2, registrationYear-3];
  const yearCounts = prevYears.map(y => regYears.filter(ry => ry === y).length);
  const previousYearsCount = regYears.filter(ry => ry < registrationYear-3).length;

  // ---- Leostic (council) year breakdown: 2020-21 through the current year,
  // computed fresh every render so it never goes stale the way the old
  // hardcoded "2025-2026" table did. Each row's member list is what "view
  // this year's new members" hooks into via the Member Details filter below.
  const currentCouncilYearStart = getCurrentCouncilYearStart();
  const councilYearOptions = useMemo(() => getCouncilYearOptions(), []);
  const councilYearBreakdown = useMemo(
    () => councilYearOptions.map(({ startYear, label }) => ({
      startYear,
      label,
      members: members.filter((m) => isDateInCouncilYear(m.RegDate, startYear)),
    })).reverse(), // most recent year first
    [councilYearOptions, members]
  );

  // ---- Graduated, estimated from each member's SLIIT ID enrollment year
  // (e.g. IT21... -> enrolled 2021 -> expected to graduate 2025) rather than
  // how long ago they joined the Leo club, which is a different date and can
  // be years off for anyone who joined after their first year. Kept as its
  // own metric alongside the existing RegDate-based `graduatesCount` above
  // instead of replacing it, since the two answer different questions.
  const graduatedBySliitId = useMemo(
    () => members.filter((m) => isLikelyGraduatedBySliitId(m.SLIITID, currentCouncilYearStart)),
    [members, currentCouncilYearStart]
  );

  // ---- Data-quality review: members whose record trips one or more
  // heuristic checks (placeholder-looking names, malformed/duplicate SLIIT
  // IDs, future dates, no contact info at all). Pattern-matching, not proof
  // -- surfaced so an admin can take a second look, not as an accusation.
  const flaggedMembers = useMemo(() => getFlaggedMembers(members), [members]);

  // ---- Presentation-only derived values for the redesigned summary cards ----
  // Club Projects metrics are not yet wired to a projects API on this page;
  // these are the same static placeholder numbers that were previously
  // hardcoded directly in the JSX below (all zero), just given names so the
  // new tile markup can reference them instead of repeating literal "0"s.
  const totalProjects = 0;
  const completedProjects = 0;
  const totalPhaseCount = 0;
  const pendingReportCount = 0;

  // Share of the current roster that joined during the active Leostic year --
  // used only to drive the decorative progress ring on the membership hero.
  const membershipGrowthPct = total > 0
    ? Math.min(100, Math.max(0, Math.round((newcomersAcademicYear / total) * 100)))
    : 0;

  return (
    <div className="min-h-screen">
      <Sidebar />
      <Topbar user={user} />

      <main className="ml-0 lg:ml-64 mt-14 px-2 sm:px-4 md:px-8 py-4 md:py-10">
        {/* Page head */}
        <div className="page-head">
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <FontAwesomeIcon icon={faChartPie} style={{ color: 'var(--accent)' }} />
              Club Dashboard <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>/ Club Statistics</span>
            </h1>
            <p className="page-desc">Membership growth, project activity, and club-wide analytics for the Leo Club of SLIIT.</p>
          </div>
        </div>

        {/* Quick shortcuts (shared across the admin/statistics pages) */}
        <div className="filter-row" style={{ flexWrap: 'wrap', marginBottom: 'var(--sp-6)' }}>
          <Link to="/club-statistics" className="chip active">
            <FontAwesomeIcon icon={faChartPie} /> Club Statistics
          </Link>
          <Link to="/reporting-hub" className="chip">
            <FontAwesomeIcon icon={faChartBar} /> Reporting Hub
          </Link>
          <Link to="/member-database" className="chip">
            <FontAwesomeIcon icon={faUsers} /> Member Database
          </Link>
          <Link to="/category-selection" className="chip">
            <FontAwesomeIcon icon={faListAlt} /> Category Selections
          </Link>
          <Link to="/tickets-admin" className="chip">
            <FontAwesomeIcon icon={faTicketAlt} /> Tickets Admin
          </Link>
        </div>

        {/* Priority strip: total membership as the wide hero metric (with a
            progress ring showing how much of the roster joined this Leostic
            year), plus the next-most-important supporting numbers alongside. */}
        <div className="priority">
          <Link to="/member-database" className="panel hero-metric hover:bg-[var(--surface-2)] transition-colors">
            <div className="hero-top">
              <span className="eyebrow">Total Membership</span>
              <div
                className="ring"
                style={{ '--pct': membershipGrowthPct }}
                data-pct={`${membershipGrowthPct}%`}
                aria-hidden="true"
              />
            </div>
            <div className="hero-value num">
              {loading ? <span className="skeleton" style={{ display: 'inline-block', width: 64, height: 34 }} /> : total}
            </div>
            <div className="hero-sub">
              <FaUsersIcon size={11} style={{ marginRight: 6 }} aria-hidden="true" />
              {loading ? 'Loading roster…' : `${membershipGrowthPct}% joined during the current Leostic year`}
            </div>
          </Link>

          <Link to="/member-database?newcomers=month" className="panel tile hover:bg-[var(--surface-2)] transition-colors">
            <span className="tile-label">New Comers · This Month</span>
            <span className="tile-value num">
              {loading ? <span className="skeleton" style={{ display: 'inline-block', width: 40, height: 26 }} /> : newcomersMonth}
            </span>
            <span className="tile-trend trend-up">
              <FaUserPlus size={11} aria-hidden="true" /> Joined this calendar month
            </span>
          </Link>

          <Link to="/member-database?newcomers=year" className="panel tile hover:bg-[var(--surface-2)] transition-colors">
            <span className="tile-label">New Comers · 2026 Jul – 2027 Jun</span>
            <span className="tile-value num">
              {loading ? <span className="skeleton" style={{ display: 'inline-block', width: 40, height: 26 }} /> : newcomersAcademicYear}
            </span>
            <span className="tile-trend trend-flat">
              <FaCalendarAlt size={11} aria-hidden="true" /> Current Leostic year
            </span>
          </Link>
        </div>

        {/* Data Quality Review -- flags entries that are likely typos, test
            rows, or duplicates rather than real members, so odd-looking
            additions get noticed soon after they're added instead of
            sitting unreviewed in the roster. Heuristic, not a verdict --
            worded that way in the UI on purpose. */}
        {!loading && flaggedMembers.length > 0 && (
          <div style={{ marginBottom: 'var(--sp-7)' }}>
            <div className="section-head" style={{ padding: 0, marginBottom: 'var(--sp-3)' }}>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FaExclamationTriangle size={14} style={{ color: 'var(--warning)' }} aria-hidden="true" />
                Data Quality Review
                <span className="pill pill-warning" style={{ marginLeft: 4 }}>
                  <span className="dot" /> {flaggedMembers.length} to review
                </span>
              </h2>
            </div>
            <div className="panel">
              <p style={{ padding: 'var(--sp-4) var(--sp-4) 0', fontSize: 12.5, color: 'var(--text-2)' }}>
                Automatic pattern checks only (placeholder-looking names, malformed or duplicate SLIIT IDs, future
                dates, missing contact info) -- a flag means "worth a second look," not a confirmed problem.
              </p>
              <div className="overflow-x-auto">
                <table>
                  <thead>
                    <tr>
                      <th>Full Name</th>
                      <th>SLIIT ID</th>
                      <th>Reason(s) flagged</th>
                    </tr>
                  </thead>
                  <tbody>
                    {flaggedMembers.slice(0, 25).map(({ member, flags }) => (
                      <tr key={member.SLIITLeoID || member.SLIITID || member.Email}>
                        <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{member.FullName || 'N/A'}</td>
                        <td className="num" style={{ whiteSpace: 'nowrap' }}>{member.SLIITID || 'N/A'}</td>
                        <td>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {flags.map((flag) => (
                              <span key={flag} className="pill pill-warning" style={{ fontSize: 11 }}>
                                {flag}
                              </span>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {flaggedMembers.length > 25 && (
                <p style={{ padding: 'var(--sp-3) var(--sp-4)', fontSize: 12, color: 'var(--text-3)' }}>
                  Showing 25 of {flaggedMembers.length} flagged members.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Club Projects */}
        <div style={{ marginBottom: 'var(--sp-7)' }}>
          <div className="section-head" style={{ padding: 0, marginBottom: 'var(--sp-3)' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaProjectDiagram size={14} style={{ color: 'var(--accent)' }} aria-hidden="true" /> Club Projects
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--sp-4)' }}>
            <div className="panel tile">
              <span className="tile-label"><FaChartPieIcon style={{ marginRight: 6 }} aria-hidden="true" />Total Projects</span>
              <span className="tile-value num">{totalProjects}</span>
              <span className="tile-trend trend-flat">Not yet linked to a project feed</span>
            </div>
            <div className="panel tile">
              <span className="tile-label"><FaCheckCircle style={{ marginRight: 6, color: 'var(--success)' }} aria-hidden="true" />Completed Projects</span>
              <span className="tile-value num">{completedProjects}</span>
              <span className="tile-trend trend-flat">Of {totalProjects} total</span>
            </div>
            <div className="panel tile">
              <span className="tile-label"><FaLayerGroup style={{ marginRight: 6, color: 'var(--link)' }} aria-hidden="true" />Total Phase Count</span>
              <span className="tile-value num">{totalPhaseCount}</span>
              <span className="tile-trend trend-flat">Across all projects</span>
            </div>
            <div className="panel tile">
              <span className="tile-label"><FaHourglassHalf style={{ marginRight: 6, color: 'var(--warning)' }} aria-hidden="true" />Pending Report Count</span>
              <span className="tile-value num">{pendingReportCount}</span>
              <span className={pendingReportCount > 0 ? 'tile-trend trend-warn' : 'tile-trend trend-flat'}>
                <FaExclamationTriangle size={11} aria-hidden="true" /> {pendingReportCount > 0 ? 'Needs attention' : 'All caught up'}
              </span>
            </div>
          </div>
        </div>

        {/* Membership by Year Level */}
        <div style={{ marginBottom: 'var(--sp-7)' }}>
          <div className="section-head" style={{ padding: 0, marginBottom: 'var(--sp-3)' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaUserGraduate size={14} style={{ color: 'var(--accent)' }} aria-hidden="true" /> Membership by Year Level
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--sp-4)' }}>
            <Link to="/member-database?year=1" className="panel tile hover:bg-[var(--surface-2)] transition-colors">
              <span className="tile-label">Year 01</span>
              <span className="tile-value num">
                {loading ? <span className="skeleton" style={{ display: 'inline-block', width: 32, height: 26 }} /> : year1Count}
              </span>
              <span className="tile-trend trend-flat"><FaMedal size={11} aria-hidden="true" /> First year</span>
            </Link>
            <Link to="/member-database?year=2" className="panel tile hover:bg-[var(--surface-2)] transition-colors">
              <span className="tile-label">Year 02</span>
              <span className="tile-value num">
                {loading ? <span className="skeleton" style={{ display: 'inline-block', width: 32, height: 26 }} /> : year2Count}
              </span>
              <span className="tile-trend" style={{ color: 'var(--link)' }}><FaMedal size={11} aria-hidden="true" /> Second year</span>
            </Link>
            <Link to="/member-database?year=3" className="panel tile hover:bg-[var(--surface-2)] transition-colors">
              <span className="tile-label">Year 03</span>
              <span className="tile-value num">
                {loading ? <span className="skeleton" style={{ display: 'inline-block', width: 32, height: 26 }} /> : year3Count}
              </span>
              <span className="tile-trend trend-warn"><FaMedal size={11} aria-hidden="true" /> Third year</span>
            </Link>
            <Link to="/member-database?year=4" className="panel tile hover:bg-[var(--surface-2)] transition-colors">
              <span className="tile-label">Year 04</span>
              <span className="tile-value num">
                {loading ? <span className="skeleton" style={{ display: 'inline-block', width: 32, height: 26 }} /> : year4Count}
              </span>
              <span className="tile-trend" style={{ color: 'var(--error)' }}><FaMedal size={11} aria-hidden="true" /> Fourth year</span>
            </Link>
            <Link to="/member-database?year=graduated" className="panel tile hover:bg-[var(--surface-2)] transition-colors" title="Members whose Leo club join date was 4+ years ago">
              <span className="tile-label">Graduates (by Join Date)</span>
              <span className="tile-value num">
                {loading ? <span className="skeleton" style={{ display: 'inline-block', width: 32, height: 26 }} /> : graduatesCount}
              </span>
              <span className="tile-trend trend-up"><FaGraduationCap size={11} aria-hidden="true" /> Since RegDate</span>
            </Link>
            <div className="panel tile" title="Estimated from each member's SLIIT ID enrollment year + 4 -- e.g. IT21... enrolled 2021, expected to graduate 2025">
              <span className="tile-label">Likely Graduated (by Student ID)</span>
              <span className="tile-value num">
                {loading ? <span className="skeleton" style={{ display: 'inline-block', width: 32, height: 26 }} /> : graduatedBySliitId.length}
              </span>
              <span className="tile-trend trend-up"><FaUserGraduate size={11} aria-hidden="true" /> Enrollment year + 4</span>
            </div>
          </div>
        </div>

        {/* Member Distribution */}
        <div style={{ marginBottom: 'var(--sp-7)' }}>
          <div className="section-head" style={{ padding: 0, marginBottom: 'var(--sp-3)' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaUsersIcon size={14} style={{ color: 'var(--accent)' }} aria-hidden="true" /> Member Distribution
            </h2>
          </div>

          {loading ? (
            <div className="panel">
              <div className="empty-state">
                <span className="spinner lg" aria-hidden="true" />
                <p>Loading member data…</p>
              </div>
            </div>
          ) : (
            <div className="split">
              <div className="panel">
                <div className="section-head">
                  <h2>Membership by Leostic Year</h2>
                </div>
                <div style={{ padding: '0 var(--sp-4) var(--sp-4)', display: 'grid', gap: 'var(--sp-5)' }}>
                  <div>
                    <div className="eyebrow" style={{ marginBottom: 8 }}>
                      {councilYearOptions[councilYearOptions.length - 1].label} Leostic Year, by student year level
                    </div>
                    <table>
                      <thead>
                        <tr>
                          <th>Year</th>
                          <th style={{ textAlign: 'right' }}>Members</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { name: '1st Year', year: 'YEAR 1', variant: 'pill-success' },
                          { name: '2nd Year', year: 'YEAR 2', variant: 'pill-warning' },
                          { name: '3rd Year', year: 'YEAR 3', variant: 'pill-info' },
                          { name: '4th Year', year: 'YEAR 4', variant: 'pill-danger' }
                        ].map((item) => {
                          const currentYearMembers = members.filter(member =>
                            isDateInCouncilYear(member.RegDate, currentCouncilYearStart) &&
                            member.Year?.toString().trim() === item.year
                          );

                          return (
                            <tr key={item.year}>
                              <td>
                                <span className={`pill ${item.variant}`}>
                                  <span className="dot" />
                                  {item.name}
                                </span>
                              </td>
                              <td className="num" style={{ textAlign: 'right' }}>
                                {currentYearMembers.length} members
                              </td>
                            </tr>
                          );
                        })}
                        <tr>
                          <td style={{ fontWeight: 700 }}>Total</td>
                          <td className="num" style={{ textAlign: 'right', fontWeight: 700, color: 'var(--link)' }}>
                            {newcomersAcademicYear} members
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div>
                    <div className="eyebrow" style={{ marginBottom: 8 }}>
                      All Leostic Years ({EARLIEST_COUNCIL_YEAR}-{String((EARLIEST_COUNCIL_YEAR + 1) % 100).padStart(2, '0')} onward)
                    </div>
                    <table>
                      <thead>
                        <tr>
                          <th>Leostic Year</th>
                          <th style={{ textAlign: 'right' }}>New Members</th>
                          <th style={{ textAlign: 'right' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {councilYearBreakdown.map(({ startYear, label, members: yearMembers }) => (
                          <tr key={startYear}>
                            <td style={{ color: startYear === currentCouncilYearStart ? 'var(--text-1)' : 'var(--text-2)', fontWeight: startYear === currentCouncilYearStart ? 700 : 400 }}>
                              {label}{startYear === currentCouncilYearStart ? ' (current)' : ''}
                            </td>
                            <td className="num" style={{ textAlign: 'right', color: 'var(--text-2)' }}>
                              {yearMembers.length} member{yearMembers.length !== 1 ? 's' : ''}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => {
                                  setSelectedYear(String(startYear));
                                  setCurrentPage(1);
                                  document.getElementById('member-details')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                              >
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="panel">
                <div className="section-head">
                  <h2>Faculties</h2>
                </div>
                <div style={{ padding: '0 var(--sp-4) var(--sp-4)' }}>
                  <div className="eyebrow" style={{ marginBottom: 8 }}>2025-26 Intake</div>
                  <table>
                    <thead>
                      <tr>
                        <th>Faculty</th>
                        <th style={{ textAlign: 'right' }}>Members</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { name: 'Faculty of Business Management', variant: 'pill-info' },
                        { name: 'Faculty of Computing', variant: 'pill-success' },
                        { name: 'Faculty of Human Sciences', variant: 'pill-warning' }
                      ].map((faculty, index) => {
                        const count = members.filter(member =>
                          member.Faculty === faculty.name &&
                          member.RegDate &&
                          new Date(member.RegDate) >= new Date('2025-07-01') &&
                          new Date(member.RegDate) <= new Date('2026-06-30')
                        ).length;

                        return (
                          <tr key={index}>
                            <td>
                              <span className={`pill ${faculty.variant}`}>
                                <span className="dot" />
                                {faculty.name}
                              </span>
                            </td>
                            <td className="num" style={{ textAlign: 'right' }}>{count}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Member Growth */}
        <div className="panel" style={{ marginBottom: 'var(--sp-7)' }}>
          <div className="section-head">
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaChartLine size={13} style={{ color: 'var(--accent)' }} aria-hidden="true" /> Member Growth
            </h2>
          </div>
          <div style={{ padding: '0 var(--sp-4) var(--sp-5)' }}>
            {/* MemberGrowthLineChart provides its own role="img"/aria-label internally */}
            <MemberGrowthLineChart data={generateMemberGrowthData(members)} />
          </div>
        </div>

        {/* Member Details */}
        <div className="panel" id="member-details">
          <div className="section-head" style={{ flexWrap: 'wrap', gap: 'var(--sp-3)' }}>
            <h2>Member Details</h2>
            <div className="filter-row" style={{ flexWrap: 'wrap' }}>
              <select
                style={{ width: 'auto', flexShrink: 0 }}
                className="text-sm"
                onChange={(e) => {
                  setSelectedYear(e.target.value);
                  setCurrentPage(1);
                }}
                value={selectedYear}
              >
                <option value="">All Leostic Years</option>
                {[...councilYearOptions].reverse().map(({ startYear, label }) => (
                  <option key={startYear} value={String(startYear)}>
                    {label} (Jul {startYear} - Jun {startYear + 1})
                  </option>
                ))}
              </select>
              <div className="relative w-full sm:w-72">
                <input
                  type="text"
                  placeholder="Search by SLIIT ID, full name, email, department..."
                  className="w-full text-sm"
                  style={{ paddingRight: 34 }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="icon-btn absolute right-1 top-1/2 -translate-y-1/2"
                    style={{ width: 24, height: 24 }}
                    aria-label="Clear search"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
                {!searchQuery && (
                  <div className="absolute right-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-3)' }} aria-hidden="true">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                )}
              </div>
              <select
                style={{ width: 'auto', flexShrink: 0 }}
                className="text-sm"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value="5">5 per page</option>
                <option value="10">10 per page</option>
                <option value="25">25 per page</option>
                <option value="50">50 per page</option>
                <option value="100">100 per page</option>
              </select>
              <button
                onClick={() => {
                  const currentYearLabel = councilYearOptions[councilYearOptions.length - 1].label;
                  const currentYearMembers = members.filter((member) => isDateInCouncilYear(member.RegDate, currentCouncilYearStart));

                  if (currentYearMembers.length === 0) {
                    toast.warning(`No members found for the ${currentYearLabel} Leostic year`, {
                      position: "top-right",
                      autoClose: 3000,
                      hideProgressBar: false,
                      closeOnClick: true,
                      pauseOnHover: true,
                      draggable: true,
                      progress: undefined,
                    });
                    return;
                  }

                  // Convert to CSV
                  const headers = ['Leo ID', 'SLIIT ID', 'Roll No', 'Full Name', 'Email', 'Faculty', 'Year', 'Registration Date', 'Likely Graduated (by Student ID)'];
                  const csvContent = [
                    headers.join(','),
                    ...currentYearMembers.map(member => [
                      `"${member.SLIITLeoID || ''}"`,
                      `"${member.SLIITID || ''}"`,
                      `"${member.RollNo || ''}"`,
                      `"${member.FullName || ''}"`,
                      `"${member.Email || ''}"`,
                      `"${member.Faculty || ''}"`,
                      `"${member.Year || ''}"`,
                      `"${member.RegDate || ''}"`,
                      `"${isLikelyGraduatedBySliitId(member.SLIITID, currentCouncilYearStart) ? 'Yes' : 'No'}"`,
                    ].join(','))
                  ].join('\n');

                  // Create and trigger download
                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.setAttribute('href', url);
                  link.setAttribute('download', `members_${currentYearLabel}_${new Date().toISOString().split('T')[0]}.csv`);
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                className="btn btn-primary btn-sm"
                title={`Export ${councilYearOptions[councilYearOptions.length - 1].label} Members`}
                aria-label="Export current Leostic year members to CSV"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="hidden sm:inline">Export {councilYearOptions[councilYearOptions.length - 1].label}</span>
              </button>
            </div>
          </div>

          <div style={{ padding: '0 var(--sp-4) var(--sp-4)' }}>
            <div className="overflow-x-auto">
              <table>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      style={{ cursor: 'pointer' }}
                      className="hover:bg-[var(--surface-2)]"
                      onClick={() => requestSort('SLIITLeoID')}
                    >
                      <div className="flex items-center">
                        Leo ID
                        {getSortIcon('SLIITLeoID')}
                      </div>
                    </th>
                    <th
                      scope="col"
                      style={{ cursor: 'pointer' }}
                      className="hover:bg-[var(--surface-2)]"
                      onClick={() => requestSort('FullName')}
                    >
                      <div className="flex items-center">
                        Full Name
                        {getSortIcon('FullName')}
                      </div>
                    </th>
                    <th
                      scope="col"
                      style={{ cursor: 'pointer' }}
                      className="hidden md:table-cell hover:bg-[var(--surface-2)]"
                      onClick={() => requestSort('SLIITID')}
                    >
                      <div className="flex items-center">
                        SLIIT ID
                        {getSortIcon('SLIITID')}
                      </div>
                    </th>
                    <th
                      scope="col"
                      style={{ cursor: 'pointer' }}
                      className="hidden lg:table-cell hover:bg-[var(--surface-2)]"
                      onClick={() => requestSort('Faculty')}
                    >
                      <div className="flex items-center">
                        Faculty
                        {getSortIcon('Faculty')}
                      </div>
                    </th>
                    <th
                      scope="col"
                      style={{ cursor: 'pointer' }}
                      className="hidden sm:table-cell hover:bg-[var(--surface-2)]"
                      onClick={() => requestSort('Year')}
                    >
                      <div className="flex items-center">
                        Year
                        {getSortIcon('Year')}
                      </div>
                    </th>
                    <th
                      scope="col"
                      style={{ cursor: 'pointer' }}
                      className="hidden lg:table-cell hover:bg-[var(--surface-2)]"
                      onClick={() => requestSort('Semester')}
                    >
                      <div className="flex items-center">
                        Semester
                        {getSortIcon('Semester')}
                      </div>
                    </th>
                    <th
                      scope="col"
                      style={{ cursor: 'pointer' }}
                      className="hover:bg-[var(--surface-2)]"
                      onClick={() => requestSort('LeoClubStatus')}
                    >
                      <div className="flex items-center">
                        Status
                        {getSortIcon('LeoClubStatus')}
                      </div>
                    </th>
                    <th
                      scope="col"
                      style={{ cursor: 'pointer' }}
                      className="hidden xl:table-cell hover:bg-[var(--surface-2)]"
                      onClick={() => requestSort('Role')}
                    >
                      <div className="flex items-center">
                        Role
                        {getSortIcon('Role')}
                      </div>
                    </th>
                    <th
                      scope="col"
                      style={{ cursor: 'pointer' }}
                      className="hidden sm:table-cell hover:bg-[var(--surface-2)]"
                      onClick={() => requestSort('RegDate')}
                    >
                      <div className="flex items-center">
                        Registered
                        {getSortIcon('RegDate')}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((member) => {
                    // Format registration date
                    const registeredDate = (() => {
                      if (!member.RegDate) return 'N/A';
                      const d = new Date(member.RegDate);
                      return isNaN(d) ? 'N/A' : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                    })();

                    return (
                      <tr key={member.SLIITLeoID}>
                        <td className="num" style={{ whiteSpace: 'nowrap' }}>
                          {member.SLIITLeoID || 'N/A'}
                        </td>
                        <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                          <div className="flex items-center">
                            <img
                              className="h-8 w-8 rounded-full mr-2"
                              style={{ background: 'var(--surface-2)' }}
                              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(member.FullName || '')}&background=7c3aed&color=fff`}
                              alt={member.FullName}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = 'https://ui-avatars.com/api/?name=U&background=7c3aed&color=fff';
                              }}
                            />
                            <span className="truncate" style={{ maxWidth: 180 }}>
                              {member.FullName || 'N/A'}
                            </span>
                          </div>
                        </td>
                        <td className="hidden md:table-cell num" style={{ whiteSpace: 'nowrap' }}>
                          <div className="flex items-center" style={{ gap: 6 }}>
                            {member.SLIITID || 'N/A'}
                            {isLikelyGraduatedBySliitId(member.SLIITID, currentCouncilYearStart) && (
                              <span
                                className="pill pill-success"
                                style={{ fontSize: 10, padding: '2px 6px' }}
                                title="Estimated from SLIIT ID enrollment year + 4"
                              >
                                Grad
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="hidden lg:table-cell" style={{ whiteSpace: 'nowrap' }}>
                          {member.Faculty || 'N/A'}
                        </td>
                        <td className="hidden sm:table-cell" style={{ whiteSpace: 'nowrap' }}>
                          {member.Year || 'N/A'}
                        </td>
                        <td className="hidden lg:table-cell" style={{ whiteSpace: 'nowrap' }}>
                          {member.Semester || 'N/A'}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span className={`pill ${
                            member.LeoClubStatus === 'Active'
                              ? 'pill-success'
                              : member.LeoClubStatus === 'Inactive'
                              ? 'pill-warning'
                              : 'pill-info'
                          }`}>
                            <span className="dot" />
                            {member.LeoClubStatus || 'N/A'}
                          </span>
                        </td>
                        <td className="hidden xl:table-cell" style={{ whiteSpace: 'nowrap' }}>
                          {member.Role || 'Club Member'}
                        </td>
                        <td className="hidden sm:table-cell num" style={{ whiteSpace: 'nowrap', color: 'var(--text-3)' }}>
                          {registeredDate}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {filteredMembers.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon" aria-hidden="true"><FaUsersIcon /></div>
                  <h3>No members found</h3>
                  <p>Try adjusting your search or academic year filter.</p>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row items-center justify-between" style={{ padding: '12px var(--sp-4)', borderTop: '1px solid var(--border)' }}>
                  <div className="mb-2 sm:mb-0" style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                    Showing <span className="num" style={{ fontWeight: 600, color: 'var(--text-1)' }}>{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                    <span className="num" style={{ fontWeight: 600, color: 'var(--text-1)' }}>
                      {Math.min(currentPage * itemsPerPage, filteredMembers.length)}
                    </span>{' '}
                    of <span className="num" style={{ fontWeight: 600, color: 'var(--text-1)' }}>{filteredMembers.length}</span> results
                  </div>
                  <div className="flex items-center" style={{ gap: 4 }}>
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="btn btn-ghost btn-sm"
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      // Show pages around current page
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      if (pageNum > totalPages) return null;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`btn btn-sm ${currentPage === pageNum ? 'btn-primary' : 'btn-ghost'}`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="btn btn-ghost btn-sm"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={toastTheme}
      />
    </div>
  );
}
